/**
 * Who is signed in, and every sentence the website says about it.
 *
 * The backing view model behind the website's account corner, its sign-in dialog and its
 * signed-out landing page. It obeys the four rules from §05 of
 * docs/kmp-sharing-architecture.html — one [StateFlow] of one [State], a [run] the
 * platform calls once, intents named for the event, and no knowledge of any platform.
 *
 * ── This is the web's model, and that is a deliberate limit ───────────────────
 *
 * Only the browser reaches this, and when the mobile screens arrive they are not expected
 * to. Signing in means something different on each side, and the difference is structural
 * rather than cosmetic:
 *
 *  - On the **web** a sign-in *gates the app*. Signed out, the chrome is a tab strip
 *    holding nothing, so the landing surface covers it and the words below are a landing
 *    page's words. The session travels as a cookie the browser attaches by itself, and
 *    there is no server to name because the bundle talks to its own origin.
 *  - On **mobile** a sign-in gates nothing. The app works with no server at all, and
 *    signing in is one step of setting up *syncing* — next to a host and port the user
 *    types and a session id the device has to keep. "Signed out" there is a settings
 *    screen, not a wall.
 *
 * So this file is not a shared model with two thirds of itself switched off on each
 * platform, and it does not pretend to be one. What *is* shared is the part that is
 * genuinely the same — the submission itself, its in-flight state and the mapping from a
 * failure to a sentence — and it lives in [SignInAttempt], which this composes and a
 * mobile model will compose too. §10 of the architecture doc gives the test that split
 * follows: not "can this be shared?" but "would both platforms want this to change at the
 * same time, in the same way?"
 *
 * The shape here is still the one-non-null-field style from §06 rather than a Kotlin
 * `sealed` hierarchy, for the reason `HelloBackingViewModel`'s header gives: a sealed
 * hierarchy crosses into Swift as a class hierarchy needing an `as?` cast per branch at
 * every read site, where nullable payloads read as a plain `if let` chain. The invariant
 * the compiler no longer enforces — exactly one field non-null — is kept by [stateFor]
 * being the only thing that builds a state, and always building a whole one.
 *
 * ── How a credential gets in ─────────────────────────────────────────────────
 *
 * [onSignInSubmitted] is the only door, and it takes the two strings somebody typed.
 * There is no second entry point: LunaPin serves one account whose credentials come from
 * the server's environment, so there is no provider to pick, no token to verify and no
 * SDK anywhere in this project. A wrong credential is an [State.SignedOut.errorMessage]
 * on the surface that asked for it, not a state of the page.
 *
 * ── Why the words live here ──────────────────────────────────────────────────
 *
 * The signed-out headline and explanation are computed here rather than written into a
 * view, because there are three of them and two are easy to forget entirely:
 *
 *  - signed out with a way in — the ordinary front door;
 *  - signed out with **nothing configured**, which is the state a fresh clone lands in,
 *    and whose card has to say sign-in is *not set up* rather than merely offering no
 *    button;
 *  - signed out because **nothing answered**. Not a state of the deployment at all but a
 *    failure to learn one, and it is separate because the fallback the repository adopts
 *    on failure is indistinguishable from the second case — see [signedOut]. Collapsed
 *    into it, as this file did until LPN-3 was reviewed, a dead server told the operator
 *    to set two environment variables they had already set.
 *
 * There used to be another — a refusal, for an account that was not this instance's owner
 * — and it is gone with the identity provider that made it possible. With one configured
 * account there is no such thing as the wrong *person* signing in, only a wrong string,
 * and a wrong string belongs in the form beside it rather than in a page-level state that
 * replaces the whole card.
 *
 * A fourth is coming and will land on the same distinction: a session the server no longer
 * accepts (*needs re-authorising*) is signed out with a way in, and must not be worded as
 * either of the two failures above.
 *
 * @see se.soderbjorn.lunapin.client.StorageRepository
 * @see SignInAttempt
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.SignInAttempt
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.readableMessage
import se.soderbjorn.lunapin.clientserver.SessionState
import se.soderbjorn.lunapin.clientserver.User

/**
 * Owns the sign-in area.
 *
 * @param storage the session's source of truth, and the only route to the server. This
 *   model deliberately does not fetch the session itself: two collectors of the same
 *   question end up disagreeing about the answer, and the shell's persistence already
 *   waits on the repository's one fetch.
 * @param scope the scope the server round-trips run on. Injected with a default so a
 *   test can supply its own and await it, rather than racing a scope it cannot see —
 *   the same arrangement `WorkspaceBackingViewModel` uses, for the same reason.
 *   `Dispatchers.Main` because every consumer is a UI.
 */
class SessionBackingViewModel(
    private val storage: StorageRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) {
    private val _stateFlow = MutableStateFlow(State(loading = State.Loading))

    /** The complete, render-ready description of the sign-in area. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * The submission itself. See [SignInAttempt] for why it is not part of this class.
     *
     * Submitting through the repository rather than through `LunaPinApi` is what replaces the
     * session and the settings cache in one step; and it is the repository that keeps the
     * browser from asking for a session id in the body, which it must never have.
     */
    private val attempt = SignInAttempt(storage::signIn, scope)

    /**
     * What a failed sign-*out* said, or null.
     *
     * Its own flow rather than a field on the published state, so that [stateFor] can stay
     * the only place a [State] is constructed. And its own flow rather than a slot it
     * shares with the sign-in failure, because the two are read in different places by
     * somebody in a different situation: a failed sign-in belongs beside the form that
     * produced it, and a failed sign-out belongs in the corner of an app that is still
     * signed in.
     */
    private val signOutError = MutableStateFlow<String?>(null)

    /**
     * Follow the session for the life of the screen. Called once by the platform; never
     * returns.
     *
     * Deliberately does not fetch: [StorageRepository.start] is the app bootstrap's job
     * and happens once per process, for the reason `HelloBackingViewModel.run` gives —
     * this model may be constructed more than once and two of them must not mean two
     * session requests.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run() {
        combine(
            storage.sessionFlow,
            attempt.stateFlow,
            signOutError,
            storage.serverUnreachableFlow,
        ) { session, attempt, signOutError, serverUnreachable ->
            stateFor(session, attempt, signOutError, serverUnreachable)
        }.collect { _stateFlow.value = it }
    }

    // ── Intents ──────────────────────────────────────────────────────────────
    //
    // None of these suspends. The state is updated synchronously and any round trip is
    // launched on this model's own scope, so a Swift call site stays free of completion
    // handlers and a Compose one needs no scope of its own. `WorkspaceBackingViewModel`
    // makes the same trade and its header explains it at length.

    /**
     * Somebody filled in the form and asked to be let in.
     *
     * Every rule about what happens next — the in-flight guard, the trim, the refusal to
     * send a blank half, and what the failure will say — is [SignInAttempt]'s, because all
     * of it is identical on a phone. This function's own contribution is one line: a fresh
     * attempt clears a stale sign-out complaint, so the corner is not still reporting the
     * previous gesture while a new one is in flight.
     *
     * @param username as typed. Trimmed downstream.
     * @param password as typed, and untouched. Not logged, not stored, and gone from this
     *   process the moment the request has been written.
     */
    fun onSignInSubmitted(username: String, password: String) {
        signOutError.value = null
        attempt.submit(username, password)
    }

    /**
     * The user changed the username or the password.
     *
     * Clears a failure message that is now describing something other than what is in the
     * boxes. The surface calls this on every keystroke; it is cheap and idempotent, and
     * making it the *view's* job to decide when a message has gone stale is how one
     * surface ends up clearing it and another not.
     */
    fun onCredentialEdited() {
        attempt.onCredentialEdited()
    }

    /** The user closed the sign-in surface without signing in. */
    fun onSignInDismissed() {
        attempt.onDismissed()
    }

    /**
     * The user asked to be signed out.
     *
     * A failed sign-out **is** reported, unlike most failures in this project. The cookie
     * survives it, so the next page load signs the same browser straight back in, and
     * somebody who is about to walk away from the machine needs to know that did not work.
     * It surfaces on [State.SignedIn.errorMessage] — the state they are still in.
     *
     * A no-op when there is no session to end, so pressing it twice costs one request
     * rather than two.
     */
    fun onSignOutClicked() {
        signOutError.value = null
        attempt.onDismissed()
        if (storage.sessionFlow.value?.user == null) return
        scope.launch {
            runCatching { storage.signOut() }
                .onFailure { cause -> signOutError.value = cause.readableMessage(SIGN_OUT_FAILED) }
        }
    }

    // ── Internals ────────────────────────────────────────────────────────────

    /**
     * The whole precedence rule, in one place.
     *
     * Ordered from most established to least, which is also the order that makes each
     * step's `return` obviously right:
     *
     *  1. The server has said who this is. Nothing outranks that.
     *  2. Somebody pressed the button and we are working on it.
     *  3. Nobody has answered yet — and nothing is said about it.
     *  4. Signed out, in whichever of the two situations this is.
     *
     * Step 2 sits above step 3 so that a press arriving before the session resolved is not
     * swallowed by the quiet state. On the web that cannot happen — the corner shows no
     * button until the session lands — and relying on that would be relying on a view to
     * hold this model's invariant.
     */
    private fun stateFor(
        session: SessionState?,
        attempt: SignInAttempt.State,
        signOutError: String?,
        serverUnreachable: Boolean,
    ): State {
        session?.user?.let { return State(signedIn = signedIn(it, signOutError)) }
        if (attempt.isSubmitting) return State(signingIn = State.SigningIn)
        if (session == null) return State(loading = State.Loading)
        return State(signedOut = signedOut(session, attempt, serverUnreachable))
    }

    private fun signedIn(user: User, signOutError: String?): State.SignedIn = State.SignedIn(
        // The username stands in for a missing display name rather than something like
        // "Signed in": this is the label for *a person*, and a username is a worse name
        // than a name but still a name.
        name = user.displayName.takeIf { it.isNotBlank() } ?: user.username,
        username = user.username,
        errorMessage = signOutError,
    )

    /**
     * The three signed-out situations, worded.
     *
     * The unreachable one is checked first because the other two read a session the
     * server never sent: [StorageRepository.start] fabricates a signed-out
     * [SessionState] when nothing answered, and its `isSignInConfigured` is false —
     * the same false a running server with no credentials sends. Ordered the other
     * way round, every server failure would go on claiming that nobody had
     * configured sign-in.
     */
    private fun signedOut(
        session: SessionState,
        attempt: SignInAttempt.State,
        serverUnreachable: Boolean,
    ): State.SignedOut = if (serverUnreachable) {
        State.SignedOut(
            // Named as a failure, because it is one — unlike the other two, which are
            // ordinary states of a working deployment. What it must not do is guess
            // whose failure: a laptop that is offline, a server that is not running, a
            // proxy in between and a 500 out of the app itself all land here, and this
            // side cannot tell them apart. So it says what is certainly true (nothing
            // answered), what follows from it (nothing is loaded), and the one thing
            // the reader can usefully do.
            headline = "Could not reach the server",
            detail = "LunaPin's server did not answer, so nothing here has loaded — " +
                "not your bookmarks, and not whether you are signed in. Check that it " +
                "is running, then reload this page.",
            // Nothing to offer: a form here would collect a password and post it at the
            // same silence, and the credential would be typed for nothing. The reload
            // in the sentence above is the action, and it is the browser's own.
            canSignIn = false,
        )
    } else if (session.isSignInConfigured) {
        State.SignedOut(
            headline = "Your bookmarks, wherever you are",
            detail = "LunaPin keeps your bookmarks — and the way you have arranged them " +
                "— on the server, so every machine you sign in from shows the same thing.",
            canSignIn = true,
            errorMessage = attempt.errorMessage,
        )
    } else {
        State.SignedOut(
            // The state a fresh clone lands in, and the one sentence that separates "I
            // have not set this up yet" from "this is broken". The two variables are
            // named outright and the document that explains them is named too:
            // somebody reading this has no other thread to pull.
            //
            // Reachable *because* the server answered. The failure case above is what
            // this used to be shown for as well, which made it an instruction to set
            // two variables, given to the one person who could see they were already
            // set.
            headline = "Sign-in is not configured",
            detail = "This LunaPin instance has no account yet. Whoever runs it sets " +
                "LUNAPIN_USERNAME and LUNAPIN_PASSWORD, and README.md explains both.",
            canSignIn = false,
            // Deliberately not carried. There is no form here to have failed, and
            // `SignInAttempt` cannot have a message in this state — nothing can be
            // submitted from a surface with no fields on it.
        )
    }

    /**
     * Exactly one field is non-null.
     *
     * @property loading the session has not answered yet, and **nothing is said about
     *   it**. The first state of every launch, and the one whose UI treatment is easiest
     *   to get wrong: the account corner renders empty space here, because flashing
     *   "Sign in…" at somebody who already has a session is a worse first impression
     *   than a beat of nothing.
     * @property signedOut nobody is signed in, with the words for whichever of the two
     *   situations that is.
     * @property signingIn somebody pressed the button. Distinct from [loading] because
     *   this one *was* asked for, so a failure out of it says so where a failure out of
     *   [loading] would be an error nobody's gesture produced.
     * @property signedIn there is an account, established by the server. The only way
     *   there is one.
     */
    data class State(
        val loading: Loading? = null,
        val signedOut: SignedOut? = null,
        val signingIn: SigningIn? = null,
        val signedIn: SignedIn? = null,
    ) {
        data object Loading

        data object SigningIn

        /**
         * @property headline the card's first line.
         * @property detail the one line of explanation under it.
         * @property canSignIn whether to offer a way in at all.
         *
         *   This is `SessionState.isSignInConfigured` under the name the surfaces actually
         *   ask by, and it is a plain field rather than something derived. It used to be
         *   derived, from the two fields that described the two sign-in methods, on the
         *   grounds that a third field which must agree with two others is a third field
         *   that can disagree with them. With one method there is nothing left to derive
         *   it from, and a `get() = isSignInConfigured` would be two names for one fact.
         *
         *   False is a real, reachable state — a server started with neither variable, or
         *   one that did not answer at all — and where it is false a surface shows **no
         *   button** rather than a disabled one. A control that cannot work reads as the
         *   app being broken, which is the impression the signed-out surface exists to
         *   prevent.
         * @property errorMessage what the last sign-in attempt failed with, or null.
         *   Belongs to the form that produced it — the web renders it inside the sign-in
         *   dialog rather than on the landing card, because the card is behind the dialog
         *   at the moment it is set, and a failure reported on both surfaces reads as two
         *   problems. Null in the nothing-configured case, where no form exists.
         */
        data class SignedOut(
            val headline: String,
            val detail: String,
            val canSignIn: Boolean,
            val errorMessage: String? = null,
        )

        /**
         * @property name what to call this person on screen — their display name, or
         *   their username where the account has no display name.
         * @property username the name this account signs in with. The account corner shows
         *   it as the name's tooltip, which is what answers "which instance am I looking
         *   at?" for somebody with a work one and a personal one open side by side.
         * @property errorMessage what a failed **sign-out** said, or null. On this state
         *   rather than on [SignedOut] because a sign-out that failed leaves the session
         *   exactly where it was: reporting it on the signed-out surface would be
         *   reporting it on a surface nobody is looking at.
         */
        data class SignedIn(
            val name: String,
            val username: String,
            val errorMessage: String? = null,
        )
    }

    private companion object {
        /** Only reached if the server refused the sign-out without saying why. */
        const val SIGN_OUT_FAILED = "Could not sign out. This browser may still be signed in."
    }
}
